import { getAcpAppName } from "@libs/acp/utils/getAcpAppName";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { getSessionIDAsync } from "redline-client-sdk";

import { useRedline } from "@libs/redline";
import { addCookie, getCookie } from "@libs/utils/helpers/getSafeCookies";

import constants from "@libs/acp/common/constants";

import { formContextMap } from "./data";
import { useGoogleAnalytics } from "./ga";
import {
	type AppInfoTrackedPayload,
	type CheckRegisterStatusEventProps,
	type DefaultFormContext,
	type DispatchedEvent,
	type ElementEventPayload,
	type ErrorResponseProps,
	type FieldEventPayload,
	type FormContinuedEventProps,
	type FormErroredProps,
	type FormName,
	FormNameEnum,
	type FormStartedEventProps,
	type FormSubmittedEventProps,
	type UserAttributeUpdatedEventProps,
	type UserAuthenticatedProps,
} from "./types";

import { type FormType, FormTypeEnum } from "@libs/acp/flows/types";

import { useOmniAds } from "@libs/utils/hooks/OmniAds";

export const useTracking = (formType: FormType) => {
	const { trackGA } = useGoogleAnalytics();
	const { track: trackRedline, getCorrelationId } = useRedline();
	const { omniTracker } = useOmniAds();

	const [formStartEventDispatch, setFormStartEventDispatch] = useState(false);
	const [inputtedEvent, setInputtedEvent] = useState<DispatchedEvent>({});

	//TODO: algumas coisas ja funcionam tendo diferenças de nomes, como é o caso desse appName, atualmente tem lugar
	// que espera AC ou CP, outros ac-app, cp-webapp etc. Por enquanto não mudaremos isso
	const appName = getAcpAppName();

	const contextName: Record<FormType, FormName> = {
		[FormTypeEnum.RECOVERY_PASSWORD]: FormNameEnum.RESET,
		[FormTypeEnum.RESET_PASSWORD]: FormNameEnum.RESET,
		[FormTypeEnum.RESET_PASSWORD_AC_FROM_CP]: FormNameEnum.RESET,
		[FormTypeEnum.SIGN_UP]: FormNameEnum.SIGN_UP,
		[FormTypeEnum.SIGN_UP_UNIFICATION]: FormNameEnum.SIGN_IN,
		[FormTypeEnum.SIGN_IN]: FormNameEnum.SIGN_IN,
		[FormTypeEnum.SIGN_IN_CROSS]: FormNameEnum.SIGN_IN_CROSS,
		[FormTypeEnum.UPDATE_CONTACT]: FormNameEnum.UPDATE_CONTACT,
		[FormTypeEnum.VALIDATE_CONFLICTING_EMAIL]:
			FormNameEnum.VALIDATE_CONFLICTING_EMAIL,
		[FormTypeEnum.UPDATE_PASSWORD]: FormNameEnum.UPDATE_PASSWORD,
		[FormTypeEnum.SIGN_UP_NCAD]: FormNameEnum.SIGN_UP_NCAD,
		[FormTypeEnum.SIGN_UP_NCAD_COMPLETED]: FormNameEnum.SIGN_UP_NCAD_COMPLETED,
		[FormTypeEnum.SIGN_IN_UNIFY_PASSWORD]: FormNameEnum.SIGN_IN_UNIFY_PASSWORD,
		[FormTypeEnum.SIGN_UP_PARTNER]: FormNameEnum.SIGN_UP_PARTNER,
		[FormTypeEnum.UNLOCK_PROTECTED_FEATURE]:
			FormNameEnum.UNLOCK_PROTECTED_FEATURE,
		[FormTypeEnum.SIMPLE_SIGN_IN]: FormNameEnum.SIMPLE_SIGN_IN,
		[FormTypeEnum.PUBLIC_EMAIL_CONFIRMATION]:
			FormNameEnum.PUBLIC_EMAIL_CONFIRMATION,
		[FormTypeEnum.PRIVATE_MFA_POSITIVE_DATA]:
			FormNameEnum.PRIVATE_MFA_POSITIVE_DATA,
	};

	const defaultFormContext: DefaultFormContext = formContextMap(
		appName as "ac" | "cp",
	)[contextName[formType]];

	function getRedlineCorrelationId() {
		return getCorrelationId(constants.redline.RL_TRACK_CID);
	}

	function fieldEvent({
		field,
		isInputtedEvent = true,
		ignoreAlreadyDispatch = false,
	}: FieldEventPayload) {
		const payload = {
			correlationId: getRedlineCorrelationId(),
			formType: contextName[formType],
			userInputField: {
				...field,
			},
			...defaultFormContext,
		};

		if (ignoreAlreadyDispatch && inputtedEvent[field.fieldName]) return;

		if (isInputtedEvent) trackRedline.userTracking.fieldInputted(payload);
		else trackRedline.userTracking.fieldSelected(payload);

		const eventName = isInputtedEvent
			? "default-fieldInputted"
			: "default-fieldSelected";

		trackGA({ eventName, payload });

		setInputtedEvent((prevState: DispatchedEvent) => ({
			...prevState,
			[field.fieldName]: true,
		}));
	}

	function pageViewEvent() {
		trackRedline.auto.pageViewed();
		trackGA({ eventName: "default-pageViewed" });
	}

	function formStartedEvent({ stepContext }: FormStartedEventProps) {
		if (formStartEventDispatch) return;
		setFormStartEventDispatch(true);

		const payload = {
			correlationId: getRedlineCorrelationId(),
			...defaultFormContext,
		};

		if (stepContext) {
			payload.stepContext = stepContext;
		}

		trackRedline.userTracking.formStarted(payload);

		const eventName = "default-formStarted";
		trackGA({ eventName, payload: defaultFormContext });
	}

	function formContinuedEvent({
		formPayload,
		stepContext,
		formContext,
	}: FormContinuedEventProps) {
		const field = Object.entries(formPayload).map(([key, _]) => {
			return { fieldName: key };
		});

		const payload = {
			correlationId: getRedlineCorrelationId(),
			field,
			...defaultFormContext,
		};

		if (stepContext) {
			payload.stepContext = stepContext;
		}

		if (formContext) {
			payload.formContext.formId = formContext.formId;
			payload.formContext.formType = formContext.formType;
		}

		trackRedline.userTracking.formContinued(payload);

		const eventName = "default-formContinued";
		trackGA({ eventName, payload: payload });
	}

	function formSubmittedEvent({
		formPayload,
		stepContext,
	}: FormSubmittedEventProps) {
		const field = Object.entries(formPayload).map(([key, _]) => {
			return { fieldName: key };
		});

		const payload = {
			correlationId: getRedlineCorrelationId(),
			field,
			...defaultFormContext,
		};

		if (stepContext) {
			payload.stepContext = stepContext;
		}

		trackRedline.userTracking.formSubmitted(payload);

		const eventName = "default-formSubmitted";
		trackGA({ eventName, payload });
	}

	function formErroredEvent({ errorResponse, stepContext }: FormErroredProps) {
		const { error_slug, message: error_message, error_code } = errorResponse;

		const payload = {
			correlationId: getRedlineCorrelationId(),
			errorDetails: error_code || "Unknown details",
			errorMessage: error_message || "Unknown message",
			errorType: error_slug || "Unknown type",
			formType: contextName[formType],
			...defaultFormContext,
		};

		if (stepContext) {
			payload.stepContext = stepContext;
		}

		trackRedline.userTracking.formErrored(payload);

		const eventName = "formErrored";
		trackGA({ eventName, payload });
	}

	function formOutcomeReceived() {
		const payload = {
			...defaultFormContext,
		};

		trackRedline.userTracking.formOutcomeReceived(payload);

		const eventName = "default-formOutcomeReceived";
		trackGA({ eventName, payload });
	}

	function elementClickedEvent(eventPayload: ElementEventPayload) {
		trackRedline.userTracking.elementClicked(eventPayload);

		const eventName = "default-elementClicked";
		trackGA({ eventName, payload: { webElement: { ...eventPayload } } });
	}

	function elementViewedEvent(eventPayload?: ElementEventPayload) {
		trackRedline.userTracking.elementViewed(eventPayload);

		const eventName = "default-elementViewed";
		trackGA({ eventName, payload: { webElement: eventPayload } });
	}

	function signUpStartedEvent(alternativeFormName?: "sign-up-from-cp") {
		const payload = {
			correlationId: getRedlineCorrelationId(),
			formType: alternativeFormName || contextName[formType],
		};
		trackRedline.userAuthentication.signUpStarted(payload);

		const eventName = "signUpStarted";
		trackGA({ eventName, payload });
	}

	function signUpCompletedEvent() {
		const payload = {
			correlationId: getRedlineCorrelationId(),
			formType: contextName[formType],
		};
		trackRedline.userAuthentication.signUpCompleted(payload);

		const eventName = "signUpCompleted";
		trackGA({ eventName, payload });
	}

	function signUpErroredEvent(errorResponse: ErrorResponseProps) {
		const { error_slug, message: error_message, error_code } = errorResponse;

		const payload = {
			correlationId: getRedlineCorrelationId(),
			errorDetails: error_code || "Unknown details",
			errorMessage: error_message || "Unknown message",
			errorType: error_slug || "Unknown type",
			formType: contextName[formType],
		};
		trackRedline.userAuthentication.signUpErrored(payload);

		const eventName = "signUpErrored";
		trackGA({ eventName, payload });
	}

	function signInErroredEvent(errorResponse: ErrorResponseProps) {
		const { error_slug, message: error_message, error_code } = errorResponse;

		const payload = {
			correlationId: getRedlineCorrelationId(),
			errorDetails: error_code || "Unknown details",
			errorMessage: error_message || "Unknown message",
			errorType: error_slug || "Unknown type",
		};
		trackRedline.userAuthentication.signInErrored(payload);

		const eventName = "signInErrored";
		trackGA({ eventName, payload });
	}

	function signInStartedEvent() {
		const payload = {
			correlationId: getRedlineCorrelationId(),
		};
		trackRedline.userAuthentication.signInStarted(payload);

		const eventName = "signInStarted";
		trackGA({ eventName, payload });
	}

	function signInCompletedEvent() {
		const payload = {
			correlationId: getRedlineCorrelationId(),
		};
		trackRedline.userAuthentication.signInCompleted(payload);

		const eventName = "signInCompleted";
		trackGA({ eventName, payload });
	}

	function checkRegisterStatusEvent(
		eventPayload?: CheckRegisterStatusEventProps,
	) {
		const payload = {
			correlationId: getRedlineCorrelationId(),
			...eventPayload,
		};
		trackRedline.userAuthentication.checkRegisterStatus(payload);

		omniTracker.all({
			google: {
				target: "gALL",
				eventName: "cpfConsult",
				conversionId: "AW-392945561/UjthCKXh24UbEJm_r7sB",
				payload: {
					...payload,
				},
			},

			meta: {
				eventName: "Custom",
				payload: {
					eventName: "cpfConsult",
					...payload,
				},
			},
		});
	}

	function userAuthenticatedEvent({
		authenticationType,
		customerId,
		isUnified = false,
	}: UserAuthenticatedProps) {
		const payload = {
			correlationId: getRedlineCorrelationId(),
			authenticationType: authenticationType,
			customerId: customerId,
			isUnified,
		};

		sessionStorage.setItem(
			constants.acp.FIRST_AUTHENTICATION_TYPE,
			authenticationType,
		);

		trackRedline.userAuthentication.userAuthenticated(payload);

		const conversionMap: Partial<
			Record<UserAuthenticatedProps["authenticationType"], string>
		> = {
			"cached-sign-in": "AW-392945561/4qFGCKGd2YUbEJm_r7sB",
			"email-sign-in": "AW-392945561/4qFGCKGd2YUbEJm_r7sB",
			"sign-in": "AW-392945561/4qFGCKGd2YUbEJm_r7sB",
			"sign-up": "AW-392945561/po4yCJ6d2YUbEJm_r7sB",
		};

		const eventName = "userAuthenticated.UserAuthenticated.v1";
		const conversionId = conversionMap[authenticationType] ?? "";

		omniTracker.all({
			google: {
				target: "gALL",
				eventName: eventName,
				conversionId: conversionId,
				payload: {
					...payload,
				},
			},

			meta: {
				eventName: "Custom",
				payload: {
					eventName: eventName,
					...payload,
				},
			},
		});
	}

	function userIdentifiedEvent(document: string, isUnified = false) {
		const payload = {
			document,
			isUnified,
		};
		trackRedline.userAuthentication.userIdentified(payload);

		const eventName = "userIdentified";
		trackGA({ eventName, payload });
	}

	function userExperimentEvent(
		experimentKey: string,
		variationValue: string,
		isControl: boolean,
	) {
		const payload = {
			experimentKey: experimentKey,
			variationValue: variationValue,
			isControl: isControl,
		};

		trackRedline.userTracking.experimentStarted(payload);

		const eventName = "experimentStarted";
		trackGA({ eventName, payload });
	}

	function appInfoTracked({
		deviceInfo,
		storeAppVersion,
	}: AppInfoTrackedPayload) {
		const payload = {
			deviceInfo,
			storeAppVersion,
		};

		const eventName = "appInfoTracked";
		trackGA({ eventName, payload });

		return trackRedline.mobile.appInfoTracked(payload);
	}

	async function appSessionStarted(mobileSessionId: string) {
		// Check if already started
		if (getCookie("rl-ass", true)) return;

		const eventName = "appSessionStarted";
		trackGA({ eventName });

		await trackRedline.mobile.appSessionStarted({
			mobileSessionId,
		});
		const webSessionId = await getSessionIDAsync();

		// Expire in 30min
		const date = new Date();
		date.setMinutes(date.getMinutes() + 30);
		const cookieData = {
			key: "rl_ass",
			value: { mobileSessionId, webSessionId },
			encrypt: true,
			expires: date,
		};

		return addCookie(cookieData);
	}

	function trackDecisionEnriched() {
		const payload = {
			properties: {
				partner: appName,
			},
		};
		trackRedline.sortingHat.decisionEnriched(payload);
	}

	function userAttributeUpdatedEvent({
		devedorId,
		customerEmail,
		customerId,
		key,
		newValue,
		oldValue,
	}: UserAttributeUpdatedEventProps) {
		const payload = {
			devedorId: devedorId,
			customer_id: customerId,
			customer_email: customerEmail,
			key: key,
			newValue: newValue,
			oldValue: oldValue,
		};

		trackRedline.userAuthentication.userAttributeUpdated(payload);
	}

	return {
		fieldEvent,
		pageViewEvent,
		formStartedEvent,
		formContinuedEvent,
		formSubmittedEvent,
		elementClickedEvent,
		formOutcomeReceived,
		elementViewedEvent,
		signUpStartedEvent,
		signUpCompletedEvent,
		signUpErroredEvent,
		signInStartedEvent,
		signInCompletedEvent,
		signInErroredEvent,
		userAuthenticatedEvent,
		userExperimentEvent,
		formErroredEvent,
		userIdentifiedEvent,
		appInfoTracked,
		appSessionStarted,
		checkRegisterStatusEvent,
		trackDecisionEnriched,
		userAttributeUpdatedEvent,
	};
};
